import { useMemo } from 'react';
import { useInvestors, useInvestmentEntities, useEntityInvestors } from './useCore';
import { useProperties, useUnits } from './useProperties';
import { useDistributions } from './useAccounting';
import { calculateOccupancyFromUnits, calculateROI } from '@/utils/sharedMetrics';
import type { InvestmentEntity, Property, InvestmentEntityInvestor } from '@repo/shared-types';

/**
 * Centralized Investor Metrics Hook
 *
 * Single source of truth for investor-derived metrics with entity-aware,
 * pro-rata aggregation. Computation is a pure reactive derivation (useMemo)
 * over already-cached React Query data so the result updates the moment any
 * source query resolves; wrapping this in useQuery previously snapshotted
 * zero-length inputs on first render and cached the zero result for minutes.
 */

export interface EntityMetrics {
  entity: InvestmentEntity;
  properties: Property[];
  ownershipPercentage: number;
  capitalInvested: number;
  noi: number;
  proRataNoi: number;
  distributions: number;
  roi: number;
  occupancyRate: number;
}

export interface InvestorMetrics {
  totalEntities: number;
  totalProperties: number;
  totalValuation: number;
  totalCapitalInvested: number;
  totalNOI: number;
  totalProRataNOI: number;
  annualizedReturn: number;
  roi: number;
  entityMetrics: EntityMetrics[];
  distributionsYTD: number;
  distributionsLifetime: number;
  distributionYield: number;
  avgOccupancy: number;
  avgCapRate: number;
  entities: InvestmentEntity[];
  properties: Property[];
  entityInvestors: InvestmentEntityInvestor[];
}

function emptyInvestorMetrics(): InvestorMetrics {
  return {
    totalEntities: 0,
    totalProperties: 0,
    totalValuation: 0,
    totalCapitalInvested: 0,
    totalNOI: 0,
    totalProRataNOI: 0,
    annualizedReturn: 0,
    roi: 0,
    entityMetrics: [],
    distributionsYTD: 0,
    distributionsLifetime: 0,
    distributionYield: 0,
    avgOccupancy: 0,
    avgCapRate: 0,
    entities: [],
    properties: [],
    entityInvestors: [],
  };
}

export const useInvestorMetrics = (investorId: string | undefined) => {
  const investorsQ = useInvestors();
  const entitiesQ = useInvestmentEntities();
  const entityInvestorsQ = useEntityInvestors(undefined, investorId);
  const propertiesQ = useProperties();
  const unitsQ = useUnits();
  const distributionsQ = useDistributions();

  const investors = investorsQ.data ?? [];
  const entities = entitiesQ.data ?? [];
  const entityInvestors = entityInvestorsQ.data ?? [];
  const allProperties = propertiesQ.data ?? [];
  const allUnits = unitsQ.data ?? [];
  const distributions = distributionsQ.data ?? [];

  const isLoading =
    !investorId ||
    investorsQ.isLoading ||
    entitiesQ.isLoading ||
    entityInvestorsQ.isLoading ||
    propertiesQ.isLoading ||
    unitsQ.isLoading ||
    distributionsQ.isLoading;

  const isError =
    investorsQ.isError ||
    entitiesQ.isError ||
    entityInvestorsQ.isError ||
    propertiesQ.isError ||
    unitsQ.isError ||
    distributionsQ.isError;

  const data = useMemo<InvestorMetrics | undefined>(() => {
    if (!investorId) return undefined;

    const investor = investors.find((i) => i.id === investorId);
    if (!investor) return emptyInvestorMetrics();

    const investorEntityRelationships = entityInvestors.filter(
      (ei) => ei.investorId === investorId,
    );
    if (investorEntityRelationships.length === 0) return emptyInvestorMetrics();

    const investorEntities = investorEntityRelationships
      .map((ei) => entities.find((e) => e.id === ei.entityId))
      .filter((e): e is InvestmentEntity => e !== undefined);

    const entityPropertyIds = investorEntities.flatMap((e) => e.propertyIds || []);
    const portfolio = allProperties.filter((p) => entityPropertyIds.includes(p.id));

    const entityMetrics: EntityMetrics[] = investorEntities.map((entity) => {
      const entityProps = allProperties.filter((p) =>
        entity.propertyIds && entity.propertyIds.includes(p.id),
      );
      const entityNOI = entityProps.reduce((sum: number, p) => sum + (p.noi || 0), 0);
      const entityInvestment = investorEntityRelationships.find(
        (ei) => ei.entityId === entity.id,
      );

      const ownershipPercentage = entityInvestment?.ownershipPercentage || 0;
      const capitalInvested = entityInvestment?.capitalInvested || 0;
      const distributionsReceived = entityInvestment?.distributionsReceived || 0;

      const proRataNoi = entityNOI * (ownershipPercentage / 100);
      const entityROI = calculateROI(proRataNoi * 12, capitalInvested);

      const entityUnits = allUnits.filter((u) => entityProps.some((p) => p.id === u.propertyId));
      const occupancyRate = calculateOccupancyFromUnits(entityUnits);

      return {
        entity,
        ownershipPercentage,
        capitalInvested,
        proRataNoi,
        occupancyRate,
        properties: entityProps,
        noi: entityNOI,
        distributions: distributionsReceived,
        roi: entityROI,
      };
    });

    const totalEntities = investorEntities.length;
    const totalProperties = portfolio.length;

    const totalValuation = portfolio.reduce(
      (sum: number, p: Property) => sum + ((p.noi || 0) * 180),
      0,
    );

    const totalCapitalInvested = entityMetrics.reduce(
      (sum: number, em) => sum + em.capitalInvested,
      0,
    );
    const totalNOI = entityMetrics.reduce((sum: number, em) => sum + em.noi, 0);
    const totalProRataNOI = entityMetrics.reduce((sum: number, em) => sum + em.proRataNoi, 0);

    const roi = calculateROI(totalProRataNOI * 12, totalCapitalInvested);
    const annualizedReturn = roi;

    const investorDistributions = distributions.filter((d) => d.investorId === investorId);
    const currentYear = new Date().getFullYear();
    const distributionsYTD = investorDistributions
      .filter((d) => new Date(d.date).getFullYear() === currentYear)
      .reduce((sum: number, d) => sum + (d.amount || 0), 0);
    const distributionsLifetime = investorDistributions.reduce(
      (sum: number, d) => sum + (d.amount || 0),
      0,
    );
    const distributionYield =
      totalCapitalInvested > 0 ? (distributionsYTD / totalCapitalInvested) * 100 : 0;

    const portfolioUnits = allUnits.filter((u) => portfolio.some((p) => p.id === u.propertyId));
    const avgOccupancy = calculateOccupancyFromUnits(portfolioUnits);

    const avgCapRate =
      portfolio.length > 0
        ? portfolio.reduce((sum: number, p) => {
            const propertyValue = (p.noi || 0) * 180;
            const capRate = propertyValue > 0 ? ((p.noi || 0) * 12 / propertyValue) * 100 : 0;
            return sum + capRate;
          }, 0) / portfolio.length
        : 0;

    return {
      totalEntities,
      totalProperties,
      totalValuation,
      totalCapitalInvested,
      totalNOI,
      totalProRataNOI,
      annualizedReturn,
      roi,
      entityMetrics,
      distributionsYTD,
      distributionsLifetime,
      distributionYield,
      avgOccupancy,
      avgCapRate: Math.round(avgCapRate * 100) / 100,
      entities: investorEntities,
      properties: portfolio,
      entityInvestors: investorEntityRelationships,
    };
  }, [
    investorId,
    investors,
    entities,
    entityInvestors,
    allProperties,
    allUnits,
    distributions,
  ]);

  return { data, isLoading, isError };
};
